import { action, computed, makeObservable, observable } from 'mobx';

import type { SupportedLanguages } from '../../i18n/constants';
import { setAnalyticsDebugTap } from '../../services/analytics/debugTap';
import { addApiCallInterceptor } from '../../services/api';
import { getCookie, getCookieDomain, removeSecureCookie, setSecureCookie } from '../../utils/secureCookies';

import {
  DEBUG_ENV_COOKIE,
  GEO_COUNTRY_OVERRIDE_HEADER,
  GEO_HEADER,
  GEO_REGION_OVERRIDE_HEADER,
  GEO_USE_GPS_HEADER,
  LS_ANALYTICS_ALERTS,
  LS_COLLAPSED_SECTIONS,
  LS_GEO,
  LS_GEO_STATE,
  LS_GEO_USE_GPS,
  LS_INDICATOR,
  LS_SUPPRESS_MARKETING,
  MAX_ANALYTICS_ALERTS,
  PR_COOKIES,
  SLUG_REGEX,
} from './constants';
import type { GeoCoordsOverride } from './geoCoordsOverride';
import { isDebugBuild, isValidGeoCoords, readGeoCoordsOverride, writeGeoCoordsOverride } from './geoCoordsOverride';

export interface DebugAnalyticsEvent {
  id: number;
  name: string;
  // Serialized when recorded, so the toast shows the payload as it was sent.
  params: string;
}

// Resolve the cookie `domain` for a WRITE, or `null` to signal the write must be
// SKIPPED (fail-closed). Debug cookies (x-debug env + PR deploys) are only written on a
// *.dev host, scoped to the dev subdomain (.dev.<registrable>, e.g.
// .dev.crowncoinscasino.com) so they route on every *.dev host but never leak to
// production — mirrors the sun-cli debug extension's cookie scoping. On any other host
// we refuse to fall back to the registrable domain (which production would receive).
function resolveWriteDomain(): string | null {
  const parts = window.location.hostname.split('.');
  const devIndex = parts.indexOf('dev');
  if (devIndex !== -1 && parts.length - devIndex >= 3) {
    return `.${parts.slice(devIndex).join('.')}`;
  }
  return null;
}

// Every domain a debug cookie may have been written to across this branch's history —
// cleared together because js-cookie only deletes an exact (name, path, domain) match.
// Covers the current dev scope, the registrable domain earlier commits used
// (via getCookieDomain), and the bare host (undefined).
function getClearDomains(): (string | undefined)[] {
  return [...new Set<string | undefined>([undefined, getCookieDomain(), resolveWriteDomain() ?? undefined])];
}

function readCollapsedSections(): Record<string, boolean> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(LS_COLLAPSED_SECTIONS) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function serializeParams(params: Record<string, unknown>): string {
  try {
    return JSON.stringify(params, null, 2);
  } catch {
    return String(params);
  }
}

// Dev-only tool: must never be active in production builds (fail-closed).
export class DebugEnvStore {
  @observable private _env: string | null = null;
  @observable private _geoCountry: string = '';
  @observable private _geoState: string = '';
  @observable private _geoUseGps: boolean = false;
  @observable private _geoCoords: GeoCoordsOverride | null = null;
  @observable private _indicatorEnabled: boolean = true;
  @observable private _analyticsAlertsEnabled: boolean = false;
  @observable private _suppressMarketing: boolean = false;
  @observable private _collapsedSections: Record<string, boolean> = {};
  // Session-only, like mobile: a reload hands the language back to the profile.
  @observable private _languageOverride: SupportedLanguages | null = null;
  @observable private _prValues: Record<string, string | null> = {};
  @observable.shallow private _analyticsEvents: DebugAnalyticsEvent[] = [];
  private nextAnalyticsEventId = 1;

  constructor() {
    makeObservable(this);
    if (!this.isEnabled) {
      return;
    }
    this._env = getCookie(DEBUG_ENV_COOKIE) || null;
    this._geoCountry = localStorage.getItem(LS_GEO) ?? '';
    this._geoState = this._geoCountry === 'US' ? (localStorage.getItem(LS_GEO_STATE) ?? '') : '';
    this._geoUseGps = localStorage.getItem(LS_GEO_USE_GPS) === 'true';
    this._geoCoords = readGeoCoordsOverride();
    this._indicatorEnabled = localStorage.getItem(LS_INDICATOR) !== 'false';
    this._analyticsAlertsEnabled = localStorage.getItem(LS_ANALYTICS_ALERTS) === 'true';
    this._suppressMarketing = localStorage.getItem(LS_SUPPRESS_MARKETING) === 'true';
    this._collapsedSections = readCollapsedSections();
    for (const { name } of PR_COOKIES) {
      this._prValues[name] = getCookie(name) ?? null;
    }

    setAnalyticsDebugTap((name, params) => this.recordAnalyticsEvent(name, params));

    // Add the geo override headers to the app's own API calls. Reads live state per request.
    // (The env needs no interceptor — the x-debug cookie rides along automatically.)
    addApiCallInterceptor((config) => {
      const headers = this.getGeoHeaders();
      for (const [key, value] of Object.entries(headers)) {
        config.headers.set(key, value);
      }
      return config;
    });
  }

  @computed
  public get isEnabled(): boolean {
    return isDebugBuild();
  }

  @computed
  public get env(): string | null {
    return this.isEnabled ? this._env : null;
  }

  @computed
  public get geoCountry(): string {
    return this.isEnabled ? this._geoCountry : '';
  }

  @computed
  public get geoState(): string {
    return this.isEnabled ? this._geoState : '';
  }

  @computed
  public get geoUseGps(): boolean {
    return this.isEnabled && this._geoUseGps;
  }

  @computed
  public get indicatorEnabled(): boolean {
    return this.isEnabled ? this._indicatorEnabled : false;
  }

  // Gated on isEnabled so a stale persisted value can never act in a production build.
  @computed
  public get analyticsAlertsEnabled(): boolean {
    return this.isEnabled && this._analyticsAlertsEnabled;
  }

  // Read by FeatureManagerStore.startDialogs to skip the lobby's auto-popup sequence.
  @computed
  public get suppressMarketing(): boolean {
    return this.isEnabled && this._suppressMarketing;
  }

  @computed
  public get languageOverride(): SupportedLanguages | null {
    return this.isEnabled ? this._languageOverride : null;
  }

  @computed
  public get collapsedSections(): Record<string, boolean> {
    return this._collapsedSections;
  }

  @computed
  public get analyticsEvents(): DebugAnalyticsEvent[] {
    return this._analyticsEvents;
  }

  // True when requests are routed somewhere non-default (debug env or a PR deploy
  // cookie) — surfaced as a dot on the floating button, same as mobile.
  @computed
  public get hasRoutingOverrides(): boolean {
    return Boolean(this.env) || Object.values(this._prValues).some(Boolean);
  }

  // Env rides as the x-debug cookie, scoped to the parent dev domain so every surface
  // (the app's own API calls, the game-ui iframe and its API calls) picks it up
  // automatically — same mechanism as the mobile app and the sun-cli debug extension.
  // The cookie is the source of truth: it's read back on init, so no localStorage copy.
  @action
  public setEnv(name: string): void {
    const trimmed = name.trim();
    if (!trimmed) {
      this.clearEnv();
      return;
    }
    // Fail-closed: never write routing cookies when disabled (e.g. production build).
    if (!this.isEnabled) {
      return;
    }
    // Fail-closed: refuse to write a routing cookie on a non-dev host.
    const domain = resolveWriteDomain();
    if (!domain) {
      return;
    }
    this._env = trimmed;
    // 30-day TTL like the PR deploy cookies so the env survives browser restarts.
    setSecureCookie(DEBUG_ENV_COOKIE, trimmed, { domain, expires: 30 });
  }

  @action
  public clearEnv(): void {
    if (!this.isEnabled) {
      return;
    }
    this._env = null;
    // Clear every scope this branch may have written to (incl. legacy .<registrable>).
    for (const domain of getClearDomains()) {
      removeSecureCookie(DEBUG_ENV_COOKIE, { domain });
    }
  }

  // State only applies to US, matching how the backend resolves territories.
  @action
  public setGeo(country: string, state: string = ''): void {
    if (!this.isEnabled) {
      return;
    }
    const normalizedCountry = country.trim().toUpperCase();
    const normalizedState = normalizedCountry === 'US' ? state.trim().toUpperCase() : '';
    this._geoCountry = normalizedCountry;
    this._geoState = normalizedState;
    localStorage.setItem(LS_GEO, normalizedCountry);
    localStorage.setItem(LS_GEO_STATE, normalizedState);
  }

  @computed
  public get geoCoords(): GeoCoordsOverride | null {
    return this.isEnabled ? this._geoCoords : null;
  }

  // Replaces the browser GPS fix sent as x-geo-coords (see getUserLocation) with login and current-geo.
  @action
  public setGeoCoords(override: GeoCoordsOverride | null): { ok: boolean; error?: string } {
    if (!this.isEnabled) {
      return { ok: false };
    }
    if (override && !isValidGeoCoords(override.latitude, override.longitude)) {
      return { ok: false, error: 'Latitude must be within ±90 and longitude within ±180' };
    }
    this._geoCoords = override;
    writeGeoCoordsOverride(override);
    return { ok: true };
  }

  @action
  public toggleIndicator(): void {
    if (!this.isEnabled) {
      return;
    }
    this._indicatorEnabled = !this._indicatorEnabled;
    localStorage.setItem(LS_INDICATOR, String(this._indicatorEnabled));
  }

  @action
  public toggleGeoUseGps(): void {
    if (!this.isEnabled) {
      return;
    }
    this._geoUseGps = !this._geoUseGps;
    localStorage.setItem(LS_GEO_USE_GPS, String(this._geoUseGps));
  }

  @action
  public toggleAnalyticsAlerts(): void {
    if (!this.isEnabled) {
      return;
    }
    this._analyticsAlertsEnabled = !this._analyticsAlertsEnabled;
    localStorage.setItem(LS_ANALYTICS_ALERTS, String(this._analyticsAlertsEnabled));
    if (!this._analyticsAlertsEnabled) {
      this._analyticsEvents = [];
    }
  }

  @action
  public toggleSuppressMarketing(): void {
    if (!this.isEnabled) {
      return;
    }
    this._suppressMarketing = !this._suppressMarketing;
    localStorage.setItem(LS_SUPPRESS_MARKETING, String(this._suppressMarketing));
  }

  @action
  public toggleSectionCollapsed(section: string): void {
    this._collapsedSections = { ...this._collapsedSections, [section]: !this._collapsedSections[section] };
    localStorage.setItem(LS_COLLAPSED_SECTIONS, JSON.stringify(this._collapsedSections));
  }

  @action
  public setLanguageOverride(language: SupportedLanguages | null): void {
    if (!this.isEnabled) {
      return;
    }
    this._languageOverride = language;
  }

  @action
  public recordAnalyticsEvent(name: string, params: Record<string, unknown>): void {
    if (!this.analyticsAlertsEnabled) {
      return;
    }
    const event = { id: this.nextAnalyticsEventId++, name, params: serializeParams(params) };
    this._analyticsEvents = [event, ...this._analyticsEvents].slice(0, MAX_ANALYTICS_ALERTS);
  }

  @action
  public dismissAnalyticsEvent(id: number): void {
    this._analyticsEvents = this._analyticsEvents.filter((event) => event.id !== id);
  }

  // Resets the whole console: env, PR deploy cookies, geo (incl. use GPS), GPS coordinates, language and both
  // toggles.
  @action
  public clearAll(): void {
    if (!this.isEnabled) {
      return;
    }
    this.clearEnv();
    for (const { name } of PR_COOKIES) {
      this.clearPrCookie(name);
    }
    this.setGeo('');
    this._geoUseGps = false;
    localStorage.setItem(LS_GEO_USE_GPS, 'false');
    this.setGeoCoords(null);
    this._languageOverride = null;
    this._analyticsAlertsEnabled = false;
    localStorage.setItem(LS_ANALYTICS_ALERTS, 'false');
    this._analyticsEvents = [];
    this._suppressMarketing = false;
    localStorage.setItem(LS_SUPPRESS_MARKETING, 'false');
  }

  // Spread onto the app's own API requests. Empty when disabled/unset. cf-ipcountry is kept
  // alongside the override pair so backends without the override flag still see the geo.
  public getGeoHeaders(): Record<string, string> {
    if (!this.isEnabled || !this._geoCountry) {
      return {};
    }
    const headers: Record<string, string> = {
      [GEO_HEADER]: this._geoCountry,
      [GEO_COUNTRY_OVERRIDE_HEADER]: this._geoCountry,
      [GEO_USE_GPS_HEADER]: String(this._geoUseGps),
    };
    if (this._geoCountry === 'US' && this._geoState) {
      headers[GEO_REGION_OVERRIDE_HEADER] = this._geoState;
    }
    return headers;
  }

  @computed
  public get prValues(): Record<string, string | null> {
    return this._prValues;
  }

  public isValidSlug(value: string): boolean {
    return SLUG_REGEX.test(value);
  }

  @action
  public applyPrCookie(name: string, value: string): { ok: boolean; error?: string } {
    // Fail-closed: never write routing cookies when disabled (e.g. production build).
    if (!this.isEnabled) {
      return { ok: false };
    }
    const trimmed = value.trim();
    const config = PR_COOKIES.find((c) => c.name === name);
    if (!config) {
      return { ok: false, error: `Unknown PR cookie: ${name}` };
    }
    if (!this.isValidSlug(trimmed)) {
      return { ok: false, error: `Invalid slug "${trimmed}" — must match [a-z][a-z0-9]{3,30}` };
    }
    // Fail-closed: refuse to write a routing cookie on a non-dev host.
    const domain = resolveWriteDomain();
    if (!domain) {
      return { ok: false, error: 'Refusing to write cookie: not a *.dev host' };
    }
    // Domain-scoped to .dev.<registrable> so it routes on the matching host. 30-day TTL
    // matches the extension so the cookie survives browser restarts.
    setSecureCookie(name, trimmed, { domain, expires: 30 });
    this._prValues[name] = trimmed;
    return { ok: true };
  }

  @action
  public clearPrCookie(name: string): void {
    // Fail-closed: no-op when disabled so the tool never touches cookies in production.
    if (!this.isEnabled) {
      return;
    }
    // Clear every scope this branch may have written to (incl. legacy .<registrable>).
    for (const domain of getClearDomains()) {
      removeSecureCookie(name, { domain });
    }
    this._prValues[name] = null;
  }
}

// Module singleton (like liveGeoStore) so the geo override interceptor is registered as soon as the
// module loads — boot requests (login/profile, current-geo) fire from render.tsx long before the
// RootStore exists, and would otherwise go out without the override headers.
export const debugEnvStore = new DebugEnvStore();
